import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { AuthFirebaseFunctionsService } from '@principle-theorem/ng-auth';
import {
  INgPrincipleConfig,
  NG_PRINCIPLE_CONFIG,
} from '@principle-theorem/ng-principle';
import {
  DialogPresets,
  extendSortingDataAccessor,
  InputSearchFilter,
  ObservableDataSource,
  TrackByFunctions,
  TypedFormControl,
} from '@principle-theorem/ng-shared';
import { Organisation } from '@principle-theorem/principle-core';
import {
  ConfigDocuments,
  ICreateDemoWorkspaceRequest,
  IDemoSpace,
  RootCollection,
  type IOrganisation,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  asColRef,
  asyncForAll,
  Firestore,
  getDoc$,
  httpsCallable,
  safeCombineLatest,
  shareReplayCold,
  slugify,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { from, type Observable } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import {
  IOrganisationAddDialogData,
  OrganisationAddDialogComponent,
  OrganisationAddFormData,
} from '../organisation-add-dialog/organisation-add-dialog.component';

interface IOrganisationListItem {
  organisation: WithRef<IOrganisation>;
  accountManagers: WithRef<IStaffer>[];
}

@Component({
  selector: 'pr-organisations',
  templateUrl: './organisations.component.html',
  styleUrls: ['./organisations.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OrganisationsComponent implements OnDestroy {
  readonly trackByStaffer = TrackByFunctions.ref<WithRef<IStaffer>>();
  readonly search = new TypedFormControl<string>('');
  readonly searchFilter: InputSearchFilter<IOrganisationListItem>;
  readonly dataSource: ObservableDataSource<IOrganisationListItem>;
  readonly displayedColumns = [
    'name',
    'region',
    'status',
    'accountManagers',
    'actions',
  ];
  readonly pageSizeOptions = [25, 50, 100];
  readonly pageSize = 25;
  isProduction: boolean;

  @ViewChild(MatSort)
  set tableSort(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  @ViewChild(MatPaginator)
  set tablePaginator(paginator: MatPaginator) {
    this.dataSource.paginator = paginator;
  }

  constructor(
    private _authFunctions: AuthFirebaseFunctionsService,
    private _dialog: MatDialog,
    private _snackBar: MatSnackBar,
    @Inject(NG_PRINCIPLE_CONFIG) private _config: INgPrincipleConfig
  ) {
    const items$ = Organisation.all$().pipe(
      switchMap((organisations) =>
        safeCombineLatest(
          organisations.map((organisation) => this._toListItem$(organisation))
        )
      ),
      shareReplayCold()
    );

    this.searchFilter = new InputSearchFilter<IOrganisationListItem>(
      items$,
      this.search.valueChanges.pipe(startWith('')),
      ['organisation.name']
    );

    this.dataSource = new ObservableDataSource(this.searchFilter.results$);
    this.dataSource.sortingDataAccessor = extendSortingDataAccessor(
      (data, sortHeaderId) => this._sortingDataAccessor(data, sortHeaderId)
    );

    this.isProduction = this._config.production;
  }

  ngOnDestroy(): void {
    this.dataSource.disconnect();
  }

  async addOrganisation(): Promise<void> {
    const data = await snapshot(
      getDoc$(
        asColRef<IDemoSpace>(RootCollection.Configuration),
        ConfigDocuments.DemoSpace
      )
    );
    const organisationData = await this._dialog
      .open<
        OrganisationAddDialogComponent,
        IOrganisationAddDialogData,
        OrganisationAddFormData
      >(OrganisationAddDialogComponent, DialogPresets.small({ data }))
      .afterClosed()
      .toPromise();

    if (!organisationData?.name.trim()) {
      return;
    }

    const alreadyExists: boolean =
      await this._authFunctions.checkOrganisationExists(
        slugify(organisationData.name.toLowerCase())
      );

    if (alreadyExists) {
      this._snackBar.open('Organisation with this name already exists');
      return;
    }

    this._snackBar.open(`Creating Organisation: ${organisationData.name}...`);
    await snapshot(
      httpsCallable<ICreateDemoWorkspaceRequest>('http-demoWorkspace-create')(
        organisationData
      )
    );
    this._snackBar.open(
      `Organisation ${organisationData.name} created successfully`
    );
  }

  private _sortingDataAccessor(
    data: IOrganisationListItem,
    sortHeaderId: string
  ): string | number | undefined {
    switch (sortHeaderId) {
      case 'name':
        return data.organisation.name;
      case 'region':
        return data.organisation.region;
      case 'status':
        return data.organisation.status;
      default:
        return;
    }
  }

  private _toListItem$(
    organisation: WithRef<IOrganisation>
  ): Observable<IOrganisationListItem> {
    return from(
      asyncForAll(organisation.accountManagers ?? [], (ref) =>
        Firestore.safeGetDoc(ref)
      )
    ).pipe(
      map((staff) => ({
        organisation,
        accountManagers: compact(staff),
      }))
    );
  }
}
